import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";

export type Lang = "lv" | "en";

const translations = {
  // Header / Nav
  "search.placeholder": { lv: "Meklēt produktus…", en: "Search products…" },
  "nav.rackets": { lv: "Raketes", en: "Rackets" },
  "nav.apparel": { lv: "Apģērbs", en: "Apparel" },
  "nav.shoes": { lv: "Apavi", en: "Shoes" },
  "nav.bags": { lv: "Somas", en: "Bags" },
  "nav.accessories": { lv: "Aksesuāri", en: "Accessories" },
  "nav.balls": { lv: "Bumbas", en: "Balls" },
  "nav.courts": { lv: "Padel Korti", en: "Padel Courts" },
  "nav.courts.tooltip": { lv: "Padel kortu meklētājs — pārlūko pieejamos laikus visos Latvijas padel klubos.", en: "Padel court finder — browse available times at all Latvian padel clubs." },
  "nav.subscription": { lv: "Abonements", en: "Subscription" },

  // Homepage
  "home.hero.subtitle": { lv: "Tavs padel tenisa veikals Latvijā", en: "Your padel shop in Latvia" },
  "home.hero.cta": { lv: "Iepirkties", en: "Shop Now" },
  "home.newest": { lv: "Jaunas preces", en: "New Arrivals" },
  "home.articles": { lv: "Jaunumi un raksti", en: "News & Articles" },
  "home.testimonials": { lv: "Pēdējās atsauksmes", en: "Latest Reviews" },

  // Products
  "products.title": { lv: "Visi produkti", en: "All Products" },
  "products.count": { lv: "produkti", en: "products" },
  "products.loading": { lv: "Ielādē produktus...", en: "Loading products..." },
  "products.none": { lv: "Produkti nav atrasti", en: "No products found" },
  "products.results_for": { lv: "Rezultāti meklējumam", en: "Results for" },

  // Sorting
  "sort.label": { lv: "Kārtot pēc", en: "Sort by" },
  "sort.newest": { lv: "Jaunākais", en: "Newest" },
  "sort.price_asc": { lv: "Cena: zema → augsta", en: "Price: low → high" },
  "sort.price_desc": { lv: "Cena: augsta → zema", en: "Price: high → low" },
  "sort.name_asc": { lv: "Nosaukums: A–Z", en: "Name: A–Z" },
  "sort.name_desc": { lv: "Nosaukums: Z–A", en: "Name: Z–A" },
  "sort.brand_asc": { lv: "Zīmols: A–Z", en: "Brand: A–Z" },
  "sort.in_stock": { lv: "Noliktavā pirmie", en: "In stock first" },

  // Filters
  "filter.categories": { lv: "Kategorijas", en: "Categories" },
  "filter.all": { lv: "Visas", en: "All" },
  "filter.price_range": { lv: "Cenu diapazons", en: "Price range" },
  "filter.brand": { lv: "Zīmols", en: "Brand" },
  "filter.in_stock": { lv: "Tikai noliktavā Rīgā", en: "In stock only" },

  // Product detail
  "product.back": { lv: "Atpakaļ uz produktiem", en: "Back to Products" },
  "product.add_to_cart": { lv: "Pievienot grozam", en: "Add to Cart" },
  "product.select_size": { lv: "Izvēlieties izmēru", en: "Select Size" },
  "product.stock": { lv: "Noliktavā", en: "Stock" },
  "product.available": { lv: "pieejami", en: "available" },
  "product.out_of_stock": { lv: "Nav noliktavā", en: "Out of stock" },
  "product.select_size_prompt": { lv: "Lūdzu izvēlieties izmēru", en: "Please select a size to continue" },
  "product.specs": { lv: "Specifikācijas", en: "Specifications" },
  "product.shipping": { lv: "Piegāde un atgriešana", en: "Shipping & Returns" },
  "product.free_shipping": { lv: "Bezmaksas piegāde virs €150", en: "Free shipping on orders over €150" },
  "product.returns": { lv: "14 dienu atgriešana", en: "14-day hassle-free returns" },
  "product.warranty": { lv: "2 gadu garantija raketēm", en: "2-year warranty on rackets" },
  "product.not_found": { lv: "Produkts nav atrasts", en: "Product not found" },
  "product.load_error": { lv: "Neizdevās ielādēt produktu", en: "Failed to load product" },
  "product.retry": { lv: "Mēģināt vēlreiz", en: "Retry" },

  // Availability
  "availability.riga": { lv: "Noliktavā Rīgā", en: "In stock — Riga" },
  "availability.europe": { lv: "Noliktavā Baltijā", en: "In stock — Baltics" },
  "availability.contact": { lv: "Sazināties par pieejamību", en: "Contact for availability" },
  "availability.europe_hint": { lv: "Piegāde 4-8 darba dienas", en: "Delivery 4-8 business days" },
  "availability.same_day": { lv: "Piegāde šodien", en: "Same-day delivery" },
  "availability.europe_dialog_title": { lv: "Piegādes informācija", en: "Delivery Information" },
  "availability.europe_dialog_desc": { lv: "Šim produktam piegāde var būt ilgāka — 4-8 darba dienas. Vai vēlaties turpināt?", en: "This product ships from our Baltic warehouse — delivery may take 4-8 business days. Continue?" },
  "availability.europe_dialog_cancel": { lv: "Atcelt", en: "Cancel" },
  "availability.qty_error": { lv: "Pieprasītais daudzums šobrīd nav pieejams. Lūdzu, izvēlieties mazāku daudzumu vai sazinieties ar mums.", en: "Requested quantity is not available at the moment. Please choose a smaller quantity or contact us." },

  // Cart
  "cart.title": { lv: "Iepirkumu grozs", en: "Shopping Cart" },
  "cart.empty": { lv: "Jūsu grozs ir tukšs", en: "Your cart is empty" },
  "cart.add_items": { lv: "Pievienojiet preces, lai sāktu", en: "Add some gear to get started" },
  "cart.shop_now": { lv: "Iepirkties", en: "Shop Now" },
  "cart.clear": { lv: "Iztīrīt grozu", en: "Clear Cart" },
  "cart.subtotal": { lv: "Starpsumma", en: "Subtotal" },
  "cart.shipping": { lv: "Piegāde", en: "Shipping" },
  "cart.free": { lv: "Bezmaksas", en: "Free" },
  "cart.free_shipping_note": { lv: "Bezmaksas piegāde pasūtījumiem virs €150", en: "Free shipping on orders over €150" },
  "cart.total": { lv: "Kopā", en: "Total" },
  "cart.checkout": { lv: "Turpināt uz apmaksu", en: "Proceed to Checkout" },
  "cart.continue": { lv: "Turpināt iepirkšanos", en: "Continue Shopping" },
  "cart.order_summary": { lv: "Pasūtījuma kopsavilkums", en: "Order Summary" },
  "cart.redirecting": { lv: "Novirza uz maksājumu...", en: "Redirecting to payment..." },
  "cart.canceled": { lv: "Apmaksa tika atcelta", en: "Checkout was canceled" },
  "cart.canceled_note": { lv: "Apmaksa tika atcelta. Jūsu grozs joprojām ir šeit.", en: "Checkout was canceled. Your cart is still here." },
  "cart.your_cart": { lv: "Jūsu grozs", en: "Your Cart" },
  "cart.shipping_at_checkout": { lv: "Piegāde tiek aprēķināta pie apmaksas", en: "Shipping calculated at checkout" },
  "cart.view_checkout": { lv: "Skatīt grozu un apmaksāt", en: "View Cart & Checkout" },

  // Auth
  "auth.signin": { lv: "Pieslēgties", en: "Sign In" },
  "auth.signup": { lv: "Reģistrēties", en: "Sign Up" },
  "auth.create_account": { lv: "Izveidot kontu", en: "Create Account" },
  "auth.email": { lv: "E-pasts", en: "Email" },
  "auth.password": { lv: "Parole", en: "Password" },
  "auth.confirm_password": { lv: "Apstiprināt paroli", en: "Confirm Password" },
  "auth.first_name": { lv: "Vārds", en: "First Name" },
  "auth.last_name": { lv: "Uzvārds", en: "Last Name" },
  "auth.phone": { lv: "Tālrunis", en: "Phone" },
  "auth.country": { lv: "Valsts", en: "Country" },
  "auth.city": { lv: "Pilsēta", en: "City" },
  "auth.address1": { lv: "Adrese 1", en: "Address Line 1" },
  "auth.address2": { lv: "Adrese 2 (neobligāti)", en: "Address Line 2 (optional)" },
  "auth.postal_code": { lv: "Pasta indekss", en: "Postal Code" },
  "auth.company_name": { lv: "Uzņēmuma nosaukums (neobligāti)", en: "Company Name (optional)" },
  "auth.vat_number": { lv: "PVN numurs (neobligāti)", en: "VAT Number (optional)" },
  "auth.accept_terms": { lv: "Piekrītu lietošanas noteikumiem un privātuma politikai", en: "I accept the Terms of Service and Privacy Policy" },
  "auth.marketing_consent": { lv: "Vēlos saņemt jaunumus un piedāvājumus", en: "I'd like to receive news and offers" },
  "auth.loading": { lv: "Ielādē...", en: "Loading..." },
  "auth.no_account": { lv: "Nav konta?", en: "Don't have an account?" },
  "auth.has_account": { lv: "Jau ir konts?", en: "Already have an account?" },
  "auth.logged_in": { lv: "Pieslēgšanās veiksmīga", en: "Logged in successfully" },
  "auth.check_email": { lv: "Pārbaudiet e-pastu, lai apstiprinātu kontu", en: "Check your email to confirm your account" },
  "auth.passwords_mismatch": { lv: "Paroles nesakrīt", en: "Passwords do not match" },
  "auth.accept_terms_error": { lv: "Jums jāpiekrīt noteikumiem", en: "You must accept the terms" },
  "auth.password_min": { lv: "Parolei jābūt vismaz 6 simboliem", en: "Password must be at least 6 characters" },
  "auth.phone_invalid": { lv: "Nederīgs tālruņa numurs", en: "Invalid phone number" },
  "auth.account_created": { lv: "Konts izveidots! Pārbaudiet e-pastu.", en: "Account created! Check your email." },
  "auth.personal_info": { lv: "Personas informācija", en: "Personal Information" },
  "auth.address_info": { lv: "Piegādes adrese", en: "Shipping Address" },
  "auth.business_info": { lv: "Uzņēmuma informācija", en: "Business Information" },

  // Account
  "account.title": { lv: "Mans konts", en: "My Account" },
  "account.logout": { lv: "Iziet", en: "Log Out" },
  "account.profile": { lv: "Profils", en: "Profile" },
  "account.display_name": { lv: "Vārds", en: "Display Name" },
  "account.phone": { lv: "Tālrunis", en: "Phone" },
  "account.save": { lv: "Saglabāt profilu", en: "Save Profile" },
  "account.saving": { lv: "Saglabā...", en: "Saving..." },
  "account.saved": { lv: "Profils saglabāts!", en: "Profile saved!" },
  "account.save_error": { lv: "Neizdevās saglabāt profilu", en: "Failed to save profile" },
  "account.orders": { lv: "Pasūtījumu vēsture", en: "Order History" },
  "account.no_orders": { lv: "Nav pasūtījumu", en: "No orders yet" },
  "account.logged_out": { lv: "Izrakstīšanās veiksmīga", en: "Logged out" },

  // Account Tabs
  "account.tab.general": { lv: "Vispārīgi", en: "General" },
  "account.tab.security": { lv: "Drošība", en: "Security" },
  "account.tab.billing": { lv: "Norēķini un abonements", en: "Billing & Subscription" },

  // Security
  "security.title": { lv: "Drošība", en: "Security" },
  "security.subtitle": { lv: "Atjauniniet paroli un drošības iestatījumus.", en: "Update your password and security settings." },
  "security.new_password": { lv: "Jauna parole", en: "New Password" },
  "security.confirm_password": { lv: "Apstiprināt jauno paroli", en: "Confirm New Password" },
  "security.update_password": { lv: "Atjaunināt paroli", en: "Update Password" },
  "security.updating": { lv: "Atjaunina…", en: "Updating…" },
  "security.password_updated": { lv: "Parole atjaunināta!", en: "Password updated successfully" },
  "security.password_min": { lv: "Parolei jābūt vismaz 6 simboliem", en: "Password must be at least 6 characters" },
  "security.passwords_mismatch": { lv: "Paroles nesakrīt", en: "Passwords do not match" },
  "security.password_error": { lv: "Neizdevās atjaunināt paroli", en: "Failed to update password" },
  "security.forgot_link": { lv: "Aizmirsu paroli?", en: "Forgot password?" },
  "security.send_reset": { lv: "Nosūtīt paroles atjaunošanas saiti", en: "Send password reset link" },

  // Forgot password dialog
  "forgot.link": { lv: "Aizmirsu paroli?", en: "Forgot password?" },
  "forgot.title": { lv: "Atjaunot paroli", en: "Reset your password" },
  "forgot.subtitle": { lv: "Ievadiet e-pasta adresi, kas saistīta ar jūsu kontu. Mēs nosūtīsim drošu paroles atjaunošanas saiti.", en: "Enter the email address connected to your account. We will send you a secure password reset link." },
  "forgot.send_link": { lv: "Nosūtīt saiti", en: "Send reset link" },
  "forgot.sending": { lv: "Nosūta…", en: "Sending…" },
  "forgot.generic_sent": { lv: "Ja šai e-pasta adresei ir izveidots konts, esam nosūtījuši paroles atjaunošanas instrukcijas.", en: "If an account exists for this email address, we have sent password reset instructions." },
  "forgot.invalid_email": { lv: "Nederīga e-pasta adrese", en: "Invalid email address" },
  "forgot.too_many": { lv: "Pārāk daudz pieprasījumu. Mēģiniet vēlreiz pēc brīža.", en: "Too many requests. Please try again shortly." },

  // Reset password page
  "reset.title": { lv: "Izveidot jaunu paroli", en: "Create new password" },
  "reset.subtitle": { lv: "Izvēlieties spēcīgu paroli, ko izmantosiet, lai pieslēgtos savam kontam.", en: "Choose a strong password to sign in to your account." },
  "reset.save": { lv: "Atjaunināt paroli", en: "Update password" },
  "reset.show": { lv: "Rādīt paroli", en: "Show password" },
  "reset.hide": { lv: "Slēpt paroli", en: "Hide password" },
  "reset.req_length": { lv: "Vismaz 8 simboli", en: "At least 8 characters" },
  "reset.req_letter": { lv: "Satur burtu", en: "Contains a letter" },
  "reset.req_number": { lv: "Satur ciparu", en: "Contains a number" },
  "reset.req_mixed": { lv: "Satur lielos un mazos burtus", en: "Mix of upper and lower case" },
  "reset.passwords_mismatch": { lv: "Paroles nesakrīt", en: "Passwords do not match" },
  "reset.weak_password": { lv: "Parole ir pārāk vāja", en: "Password is too weak" },
  "reset.same_as_old": { lv: "Jaunā parole nedrīkst būt tāda pati kā vecā", en: "New password must be different from the old one" },
  "reset.unexpected": { lv: "Neizdevās atjaunināt paroli. Lūdzu, mēģiniet vēlreiz.", en: "Could not update password. Please try again." },
  "reset.invalid_title": { lv: "Saite nav derīga", en: "Link is invalid or expired" },
  "reset.invalid_body": { lv: "Šī paroles atjaunošanas saite ir nederīga, jau izmantota vai tai beidzies derīguma termiņš. Lūdzu, pieprasiet jaunu saiti.", en: "This password reset link is invalid, already used, or has expired. Please request a new one." },
  "reset.back_to_login": { lv: "Atpakaļ uz pieslēgšanos", en: "Back to sign in" },
  "reset.success_title": { lv: "Parole atjaunināta", en: "Password updated" },
  "reset.success_body": { lv: "Jūsu parole ir veiksmīgi nomainīta. Tagad varat pieslēgties ar jauno paroli.", en: "Your password has been successfully changed. You can now sign in with your new password." },
  "reset.success_toast": { lv: "Parole veiksmīgi nomainīta", en: "Password successfully changed" },
  "reset.go_to_login": { lv: "Pieslēgties", en: "Sign in" },

  "general.close": { lv: "Aizvērt", en: "Close" },



  // Order Detail
  "order.detail.title": { lv: "Pasūtījums", en: "Order" },
  "order.detail.estimated_delivery": { lv: "Paredzamā piegāde", en: "Estimated delivery" },
  "order.detail.shipping_address": { lv: "Piegādes adrese", en: "Shipping Address" },
  "order.detail.items": { lv: "Preces", en: "Items" },
  "order.detail.no_items": { lv: "Preces nav atrastas.", en: "No items found." },
  "order.detail.subtotal": { lv: "Starpsumma", en: "Subtotal" },
  "order.detail.discount": { lv: "Atlaide", en: "Discount" },
  "order.detail.shipping": { lv: "Piegāde", en: "Shipping" },
  "order.detail.free": { lv: "Bezmaksas", en: "Free" },
  "order.detail.total": { lv: "Kopā", en: "Total" },
  "order.detail.questions": { lv: "Jautājumi? Sazinieties info@padelcorner.lv", en: "Questions? Contact info@padelcorner.lv" },

  // Billing & Subscription
  "billing.title": { lv: "Abonementa pārvaldība", en: "Subscription Management" },
  "billing.subtitle": { lv: "Pārvaldiet plānu, norēķinu informāciju un abonementa iestatījumus.", en: "Manage your plan, billing details, and subscription preferences." },
  "billing.current_plan": { lv: "Pašreizējais plāns", en: "Current Plan" },
  "billing.no_active_sub": { lv: "Nav aktīva abonementa", en: "No active subscription" },
  "billing.no_plan": { lv: "Nav plāna", en: "No Plan" },
  "billing.active": { lv: "Aktīvs", en: "Active" },
  "billing.canceled": { lv: "Atcelts", en: "Canceled" },
  "billing.access_until": { lv: "Pieeja līdz: ", en: "Access until: " },
  "billing.next_billing": { lv: "Nākamais norēķins: ", en: "Next billing date: " },
  "billing.update_payment": { lv: "Atjaunināt maksājuma veidu", en: "Update Payment Method" },
  "billing.portal_error": { lv: "Neizdevās atvērt norēķinu portālu.", en: "Failed to open billing portal. Please try again." },
  "billing.address": { lv: "Norēķinu adrese", en: "Billing Address" },
  "billing.address_desc": { lv: "Šī adrese tiks izmantota rēķiniem, veicot abonēšanu vai jaunināšanu.", en: "This address will be used for invoicing when you subscribe or upgrade." },
  "billing.use_registered": { lv: "Izmantot reģistrēto adresi norēķiniem", en: "Use my registered address for billing" },
  "billing.street": { lv: "Ielas adrese", en: "Street Address" },
  "billing.address2": { lv: "Adrese 2", en: "Address Line 2" },
  "billing.city": { lv: "Pilsēta", en: "City" },
  "billing.postal_code": { lv: "Pasta indekss", en: "Postal Code" },
  "billing.country": { lv: "Valsts", en: "Country" },
  "billing.change_plan": { lv: "Mainīt plānu", en: "Change Plan" },
  "billing.choose_plan": { lv: "Izvēlieties plānu", en: "Choose a Plan" },
  "billing.monthly": { lv: "Mēnesī", en: "Monthly" },
  "billing.annually": { lv: "Gada", en: "Annually" },
  "billing.billed_annually": { lv: "Rēķins gadā", en: "Billed annually" },
  "billing.save_percent": { lv: "Ietaupiet", en: "Save" },
  "billing.your_plan": { lv: "Jūsu plāns", en: "Your Plan" },
  "billing.most_popular": { lv: "Populārākais", en: "Most Popular" },
  "billing.current_plan_btn": { lv: "Pašreizējais plāns", en: "Current Plan" },
  "billing.upgrade": { lv: "Uzlabot", en: "Upgrade" },
  "billing.downgrade": { lv: "Pazemināt", en: "Downgrade" },
  "billing.subscribe": { lv: "Abonēt", en: "Subscribe" },
  "billing.plan_unavailable": { lv: "Šis plāns vēl nav pieejams", en: "This plan is not yet available" },
  "billing.checkout_error": { lv: "Neizdevās ielādēt maksājumu.", en: "Failed to load checkout. Please try again." },
  "billing.cancel_title": { lv: "Atcelt abonementu", en: "Cancel Subscription" },
  "billing.cancel_warning": { lv: "Pēc atcelšanas jūs zaudēsiet pieeju premium funkcijām norēķinu perioda beigās.", en: "Once you cancel, you will lose access to premium features at the end of your current billing cycle." },
  "billing.cancel_confirm_title": { lv: "Vai tiešām vēlaties atcelt?", en: "Are you sure you want to cancel?" },
  "billing.cancel_confirm_desc": { lv: "Mums žēl, ka jūs aizejat! Ja atcelsiet, dati tiks saglabāti 30 dienas, bet jūs zaudēsiet pieeju premium funkcijām.", en: "We'd hate to see you go! If you cancel, your data will be retained for 30 days but you will lose access to premium features and exclusive member benefits." },
  "billing.cancel_reason": { lv: "Kāpēc jūs aizejat?", en: "Why are you leaving?" },
  "billing.reason_placeholder": { lv: "Izvēlieties iemeslu (neobligāti)", en: "Select a reason (optional)" },
  "billing.reason_expensive": { lv: "Pārāk dārgs", en: "Too expensive" },
  "billing.reason_features": { lv: "Trūkst funkciju", en: "Missing features" },
  "billing.reason_not_using": { lv: "Neizmantoju pietiekami", en: "Not using it enough" },
  "billing.reason_other": { lv: "Cits", en: "Other" },
  "billing.keep_plan": { lv: "Nē, paturēt plānu", en: "Nevermind, keep my plan" },
  "billing.yes_cancel": { lv: "Jā, atcelt abonementu", en: "Yes, cancel subscription" },
  "billing.cancel_success": { lv: "Jūsu abonements ir atcelts.", en: "Your subscription has been canceled." },
  "billing.cancel_error": { lv: "Neizdevās atcelt abonementu.", en: "Failed to cancel subscription. Please try again." },

  // Contact
  "contact.title": { lv: "Sazinies ar mums", en: "Contact Us" },
  "contact.intro": { lv: "Jautājums par padel ekipējumu vai pasūtījumu? Mēs esam šeit, lai palīdzētu!", en: "Have a question about padel gear or need help with an order? We're here to help!" },
  "contact.name": { lv: "Vārds", en: "Name" },
  "contact.subject": { lv: "Tēma", en: "Subject" },
  "contact.message": { lv: "Ziņa", en: "Message" },
  "contact.send": { lv: "Sūtīt ziņu", en: "Send Message" },
  "contact.sending": { lv: "Sūta...", en: "Sending..." },
  "contact.sent_title": { lv: "Ziņa nosūtīta!", en: "Message Sent!" },
  "contact.sent_text": { lv: "Mēs atbildēsim pēc iespējas ātrāk.", en: "We'll reply to you as soon as possible." },
  "contact.another": { lv: "Sūtīt vēl vienu ziņu", en: "Send Another Message" },
  "contact.location": { lv: "Atrašanās vieta", en: "Location" },

  // Footer
  "footer.shop": { lv: "Veikals", en: "Shop" },
  "footer.help": { lv: "Palīdzība", en: "Help" },
  "footer.contacts": { lv: "Kontakti", en: "Contacts" },
  "footer.legal": { lv: "Juridiskā info", en: "Legal" },
  "footer.tagline": { lv: "Tavs padel veikals Rīgā. Augstākā līmeņa ekipējums katram spēlētājam.", en: "Your padel shop in Riga. Top-tier gear for every player." },
  "footer.faq": { lv: "BUJ", en: "FAQ" },
  "footer.customer_service": { lv: "Klientu serviss", en: "Customer Service" },
  "footer.shipping_returns": { lv: "Piegāde un atgriešana", en: "Shipping & Returns" },
  "footer.rights": { lv: "Visas tiesības aizsargātas.", en: "All rights reserved." },

  // FAQ
  "faq.title": { lv: "Bieži uzdotie jautājumi", en: "Frequently Asked Questions" },
  "faq.subtitle": { lv: "Viss, kas jāzina par iepirkšanos Padel Corner.", en: "Everything you need to know about shopping at Padel Corner." },
  "faq.still_questions": { lv: "Joprojām ir jautājumi?", en: "Still have questions?" },
  "faq.team_help": { lv: "Mūsu komanda ir gatava palīdzēt.", en: "Our team is happy to help." },

  // Shipping & Returns
  "sr.title": { lv: "Piegāde un atgriešana", en: "Shipping & Returns" },
  "sr.shipping": { lv: "Piegāde", en: "Shipping" },
  "sr.returns": { lv: "Atgriešana", en: "Returns" },
  "sr.ship_from": { lv: "Mēs sūtām no Rīgas, Latvija", en: "We ship from Riga, Latvia" },
  "sr.ship_from_desc": { lv: "Visi pasūtījumi tiek nosūtīti no mūsu noliktavas Rīgā.", en: "All orders are dispatched from our warehouse in Riga." },
  "sr.delivery_times": { lv: "Piegādes laiki", en: "Delivery times" },
  "sr.shipping_rates": { lv: "Piegādes tarifi", en: "Shipping rates" },
  "sr.return_policy": { lv: "14 dienu atgriešanas politika", en: "14-day return policy" },
  "sr.return_desc": { lv: "Mēs pieņemam atgriešanu 14 dienu laikā pēc piegādes nelietotām precēm oriģinālajā iepakojumā.", en: "We accept returns within 14 days of delivery for unused items in their original packaging." },
  "sr.how_to_return": { lv: "Kā atgriezt", en: "How to return" },
  "sr.refunds": { lv: "Atmaksas", en: "Refunds" },
  "sr.refunds_desc": { lv: "Atmaksas tiek apstrādātas 5–7 darba dienu laikā pēc atgrieztās preces saņemšanas.", en: "Refunds are processed within 5–7 business days after we receive the returned item." },
  "sr.need_help": { lv: "Nepieciešama palīdzība ar atgriešanu?", en: "Need help with a return?" },
  "sr.business_days": { lv: "darba dienas", en: "business days" },

  // Sale
  "sale.title": { lv: "Izpārdošana", en: "Sale" },
  "sale.badge": { lv: "Līdz -40%", en: "Up to -40%" },
  "sale.loading": { lv: "Ielādē izpārdošanas produktus...", en: "Loading sale products..." },
  "sale.none": { lv: "Pašlaik nav izpārdošanas produktu. Pārbaudiet drīzumā!", en: "No sale products at the moment. Check back soon!" },

  // Order success
  "order.loading": { lv: "Ielādē pasūtījumu…", en: "Loading your order…" },
  "order.no_session": { lv: "Sesija nav atrasta", en: "No Session Found" },
  "order.no_session_desc": { lv: "Izskatās, ka esat nonācis šeit bez pabeigtas apmaksas.", en: "It looks like you navigated here without completing a checkout." },
  "order.confirmed": { lv: "Pasūtījums apstiprināts!", en: "Order Confirmed!" },
  "order.thanks": { lv: "Paldies par pirkumu", en: "Thank you for your purchase" },
  "order.received": { lv: "Maksājums saņemts!", en: "Payment Received!" },
  "order.processing": { lv: "Jūsu pasūtījums tiek apstiprināts.", en: "Your order is being confirmed." },
  "order.items": { lv: "Preces", en: "Items" },
  "order.browse": { lv: "Pārlūkot produktus", en: "Browse Products" },

  // General
  "general.loading": { lv: "Ielādē...", en: "Loading..." },
  "general.error": { lv: "Kļūda", en: "Error" },
  "general.try_again": { lv: "Mēģināt vēlreiz", en: "Try Again" },
  "general.contact_us": { lv: "Sazinies ar mums", en: "Contact Us" },

  // Promo codes
  "promo.apply": { lv: "Pielietot", en: "Apply" },
  "promo.placeholder": { lv: "Promo kods", en: "Promo code" },
  "promo.applied": { lv: "Promo kods pielietots!", en: "Promo code applied!" },
  "promo.removed": { lv: "Promo kods noņemts", en: "Promo code removed" },
  "promo.invalid": { lv: "Nederīgs promo kods", en: "Invalid promo code" },
  "promo.discount": { lv: "Atlaide", en: "Discount" },
  "cart.vat_included": { lv: "Cenā iekļauts 21% PVN", en: "Prices include 21% VAT" },

  // Admin (keep in English as admin-facing)
  "admin.title": { lv: "Admin Panel", en: "Admin Panel" },
} as const;

type TranslationKey = keyof typeof translations;

interface LanguageContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: TranslationKey) => string;
  getCategoryName: (category: { name: string; name_en?: string | null }) => string;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

const STORAGE_KEY = "padel-corner-lang";

function getInitialLang(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "lv" || stored === "en") return stored;
  } catch {}
  return "lv";
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(getInitialLang);

  const setLang = useCallback((newLang: Lang) => {
    setLangState(newLang);
    try {
      localStorage.setItem(STORAGE_KEY, newLang);
      document.cookie = `padel-lang=${newLang};path=/;max-age=${365 * 24 * 60 * 60}`;
    } catch {}
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback((key: TranslationKey): string => {
    const entry = translations[key];
    if (!entry) return key;
    return entry[lang] || entry.en || entry.lv || key;
  }, [lang]);

  const getCategoryName = useCallback((category: { name: string; name_en?: string | null }): string => {
    if (lang === "en" && category.name_en) return category.name_en;
    return category.name;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, getCategoryName }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
